import { afterNextRender, ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, input, OnInit, output, signal, viewChild } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslocoDirective } from '@jsverse/transloco';
import { LOANDEPOT_COPYRIGHT_START_YEAR } from '@ld.consumer-portal/constants';
import { ThemeService } from '@ld.consumer-portal/utils';

function nativeBrowserEmailValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) {
    return null;
  }

  const tempInput = document.createElement('input');
  tempInput.type = 'email';
  tempInput.value = control.value;

  // Use the native input only to detect an invalid format; its validationMessage is untranslatable
  // browser UI text (locale follows the browser, not the app), so the template renders a Transloco
  // key instead of this message.
  if (!tempInput.validity.valid) {
    return { nativeEmailError: true };
  }

  return null;
}

export interface LoginFormData {
  email: string;
  password?: string;
  rememberEmail: boolean;
  useFaceId: boolean;
}

@Component({
  selector: 'ld-login-card',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    TranslocoDirective,
  ],
  templateUrl: './login-card.component.html',
  styleUrl: './login-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginCardComponent implements OnInit {
  private fb = inject(FormBuilder);
  private themeService = inject(ThemeService);

  private readonly emailInputRef = viewChild<ElementRef<HTMLInputElement>>('emailInput');

  constructor() {
    afterNextRender(() => {
      if (!this.isNativePlatform()) {
        this.emailInputRef()?.nativeElement.focus();
      }
    });

    effect(() => {
      const email = this.initialEmail();
      if (email && !this.loginForm.get('email')?.value) {
        this.loginForm.patchValue({ email });
        if (this.isNativePlatform()) {
          this.rememberEmail.set(true);
        }
      }
    });
  }

  protected logoUrl = computed(() => this.themeService.getLogoUrl());

  protected isJvTheme = computed(() => this.themeService.isJvThemeActive());

  protected partnerDisplayName = computed(() => this.themeService.getPartnerDisplayName());

  protected nmlsId = computed(() => this.themeService.getNmlsId());

  protected nmlsCopyright = computed(
    () => `NMLS#${this.nmlsId()} \u00A9${LOANDEPOT_COPYRIGHT_START_YEAR}-${new Date().getFullYear()} loanDepot.com, LLC.`,
  );

  showMobileOptions = input(false);

  useBorderedInputs = input(false);

  initialEmail = input('');

  isLoading = input(false);

  externalErrorMessage = input<string | null>(null);

  isCiamEnabled = input(false);

  isNativePlatform = input(false);

  isEmailDisabled = input(false);

  biometricAvailable = input(false);

  biometricEnabled = input(false);

  biometricLabel = input('Sign in with Face ID');

  biometricIcon = input('fingerprint');

  showBiometricLogin = input(false);

  biometricLoginInProgress = input(false);

  registerClick = output<void>();

  signupClick = output<void>();

  forgotPasswordClick = output<void>();

  login = output<LoginFormData>();

  biometricLogin = output<void>();

  useEmailInstead = output<void>();

  hidePassword = signal(true);
  private internalLoading = signal(false);
  loading = computed(() => this.isLoading() || this.internalLoading());
  private internalErrorMessage = signal<string | null>(null);
  errorMessage = computed(() => this.externalErrorMessage() ?? this.internalErrorMessage());
  rememberEmail = signal(false);
  useFaceId = signal(false);
  formSubmitted = signal(false);

  loginForm = this.fb.group(
    {
      email: ['', [Validators.required, nativeBrowserEmailValidator]],
      password: ['', [Validators.required, Validators.minLength(8)]],
    },
    { updateOn: 'blur' },
  );

  ngOnInit(): void {
    if (this.isNativePlatform()) {
      this.rememberEmail.set(true);
    }

    if (this.biometricEnabled()) {
      this.useFaceId.set(true);
    }

    const email = this.initialEmail();
    if (email) {
      this.loginForm.patchValue({ email });
      if (this.isNativePlatform()) {
        this.rememberEmail.set(true);
      }
    }

    if (this.isEmailDisabled()) {
      this.loginForm.get('email')?.disable();
    }

    if (this.isCiamEnabled()) {
      this.loginForm.get('password')?.clearValidators();
      this.loginForm.get('password')?.updateValueAndValidity();
    }
  }

  togglePasswordVisibility(): void {
    this.hidePassword.update((hide) => !hide);
  }

  toggleRememberEmail(): void {
    this.rememberEmail.update((value) => !value);
  }

  toggleFaceId(): void {
    this.useFaceId.update((value) => !value);
  }

  onBiometricLogin(): void {
    this.biometricLogin.emit();
  }

  onUseEmailInstead(): void {
    this.useEmailInstead.emit();
  }

  onRegisterClick(): void {
    this.registerClick.emit();
  }

  onSignupClick(): void {
    this.signupClick.emit();
  }

  onForgotPasswordClick(): void {
    this.forgotPasswordClick.emit();
  }

  onEnterKey(event: Event): void {
    event.preventDefault();
    this.syncAutofillValues();
    this.loginForm.markAllAsTouched();
    this.loginForm.updateValueAndValidity();
    if (this.loginForm.valid) {
      this.onSubmit();
    }
  }

  private syncAutofillValues(): void {
    const emailInput = document.getElementById('email-input') as HTMLInputElement;

    if (emailInput?.value && emailInput.value !== this.loginForm.get('email')?.value) {
      this.loginForm.get('email')?.setValue(emailInput.value);
    }

    if (!this.isCiamEnabled()) {
      const passwordInput = document.getElementById('password-input') as HTMLInputElement;
      if (passwordInput?.value && passwordInput.value !== this.loginForm.get('password')?.value) {
        this.loginForm.get('password')?.setValue(passwordInput.value);
      }
    }
  }

  async onSubmit(): Promise<void> {
    this.syncAutofillValues();
    this.formSubmitted.set(true);

    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.internalErrorMessage.set(null);

    const formValue = this.loginForm.getRawValue();
    const loginData: LoginFormData = {
      email: formValue.email ?? '',
      password: formValue.password ?? '',
      rememberEmail: this.rememberEmail(),
      useFaceId: this.useFaceId(),
    };

    this.login.emit(loginData);
  }

  setLoading(isLoading: boolean): void {
    this.internalLoading.set(isLoading);
  }

  setError(message: string | null): void {
    this.internalErrorMessage.set(message);
    this.internalLoading.set(false);
  }

  reset(): void {
    this.loginForm.reset();
    this.internalErrorMessage.set(null);
    this.internalLoading.set(false);
    this.formSubmitted.set(false);
  }
}
